/**
 * BrandedFaultRoute — single Lightning-quality primitive for every fault path.
 *
 * Round 5 Item 7 (2026-06-25). The cufflinks-pass principle: dead-end + accidental-route
 * + interaction-detail surfaces should feel like the same polished product. Cloudflare's
 * stock gray 404 / 500 / "this site can't be reached" pages are NOT us; every fault path
 * renders through this component instead.
 *
 * Shape mirrors the Lightning record-detail hero (icon + headline + lead copy + CTA cluster):
 *   - kind drives the icon color tile (info / warning / error / locked)
 *   - eyebrow is the small tag above the headline (e.g., "404", "Permission needed")
 *   - title is the H1 — one sentence, plain English
 *   - body is the explanatory copy — at most 2 sentences
 *   - actions is the CTA cluster — primary + optional secondary
 *
 * Every fault route in the app gets composed from THIS component. Don't duplicate the
 * hero pattern per fault. Don't reach for Cloudflare's stock page. If a new fault state
 * emerges, add a wrapper route component that calls <BrandedFaultRoute /> with the
 * right copy.
 *
 * The visual frame uses lightning-highlight styling (matches every record detail page)
 * so a fault state feels like "a page that's just empty" rather than "the app broke".
 *
 * Inspired by Stripe's friendly fault pages — calm tone, clear next action, no
 * apologies-with-jazz-hands.
 */

import { Button } from '@olytic/foundation';
import type { ReactNode } from 'react';

import { RouterNavLink } from '../lib/foundationLinks.tsx';
import { ObjectIcon, type ObjectKind } from './ObjectIcon.tsx';

export type FaultKind = 'info' | 'warning' | 'error' | 'locked';

/**
 * Map a fault kind to a sensible default ObjectIcon. Wrapper routes can
 * override via the explicit `iconKind` prop when a specific glyph reads
 * better (e.g., 'connectors' on an OAuth fault).
 */
const KIND_TO_ICON: Record<FaultKind, ObjectKind> = {
  info: 'home',
  warning: 'activity',
  error: 'activity',
  locked: 'permission-sets',
};

interface BrandedFaultRouteProps {
  /** Severity tone — drives the icon tile color, NOT the page chrome. */
  kind: FaultKind;
  /** Optional override for the icon glyph. Defaults to KIND_TO_ICON[kind]. */
  iconKind?: ObjectKind;
  /** Small tag above the title — e.g., "404", "Permission needed", "Signed out". */
  eyebrow: string;
  /** One-sentence headline. Plain English, no "Oops!" or "Whoops!". */
  title: string;
  /** Explanatory copy. At most 2 sentences. */
  body: string;
  /** Primary CTA + optional secondary CTA[]. Renders inline. */
  actions?: ReactNode;
  /** Optional small print below the CTAs — link to docs, status page, etc. */
  fineprint?: ReactNode;
}

export function BrandedFaultRoute({
  kind,
  iconKind,
  eyebrow,
  title,
  body,
  actions,
  fineprint,
}: BrandedFaultRouteProps) {
  const resolvedIconKind = iconKind ?? KIND_TO_ICON[kind];

  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
      }}
    >
      <div
        style={{
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 18,
        }}
      >
        <ObjectIcon kind={resolvedIconKind} size="lg" variant="soft" />
        <div
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontWeight: 600,
            color: 'var(--fg-muted)',
          }}
        >
          {eyebrow}
        </div>
        <h1
          style={{
            fontSize: 24,
            fontWeight: 600,
            color: 'var(--olytic-obsidian)',
            margin: 0,
            lineHeight: 1.3,
          }}
        >
          {title}
        </h1>
        <p
          style={{
            fontSize: 14,
            color: 'var(--fg-muted)',
            margin: 0,
            lineHeight: 1.55,
            maxWidth: 380,
          }}
        >
          {body}
        </p>
        {actions !== undefined && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              marginTop: 4,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {actions}
          </div>
        )}
        {fineprint !== undefined && (
          <div
            style={{
              marginTop: 8,
              fontSize: 12,
              color: 'var(--fg-muted)',
            }}
          >
            {fineprint}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Convenience helper for the most-common CTA shape — primary "Go home" link.
 * Pass to <BrandedFaultRoute actions={...} /> when the route has no
 * particular next-step besides returning to the workspace.
 */
// Phase 1.38 (Ean dogfood 2026-06-30): both fault CTAs share an explicit
// inline-flex box so the <Link>-as-<a> primary and <button> secondary
// render at identical heights. Without this, browser default vertical
// metrics on <button> shrink it ~3px shorter than the anchor + create
// the visible size mismatch.
const FAULT_CTA_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textDecoration: 'none',
  boxSizing: 'border-box',
  lineHeight: 1,
} as const;

export function FaultCtaHome({ label = 'Back to Home' }: { label?: string }) {
  return (
    <Button asChild variant="brand" size="sm" style={FAULT_CTA_STYLE}>
      <RouterNavLink href="/home">{label}</RouterNavLink>
    </Button>
  );
}

/**
 * Convenience CTA — secondary "Try again" link that triggers a window.reload().
 * Used by network + 500 surfaces where the right next action is just to retry.
 */
export function FaultCtaRetry({ label = 'Try again' }: { label?: string }) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      style={FAULT_CTA_STYLE}
      onClick={() => window.location.reload()}
    >
      {label}
    </Button>
  );
}
